'use client'

import * as React from 'react'
import { Toast as ToastPrimitive } from '@base-ui/react/toast'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

function ToastProvider({
  limit = 1,
  ...props
}: ToastPrimitive.Provider.Props) {
  return (
    <ToastPrimitive.Provider
      data-slot="toast-provider"
      limit={limit}
      {...props}
    />
  )
}

function ToastViewport({
  className,
  ...props
}: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      className={cn(
        'pointer-events-none fixed top-0 z-100 flex max-h-screen w-full flex-col-reverse p-4 sm:top-auto sm:right-0 sm:bottom-0 sm:flex-col md:max-w-[420px]',
        className,
      )}
      {...props}
    />
  )
}

function ToastPortal({ ...props }: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />
}

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-md border p-4 pr-11 shadow-lg transition-all data-ending-style:opacity-0 data-ending-style:slide-out-to-right-full data-starting-style:animate-in data-starting-style:fade-in-0 data-starting-style:slide-in-from-top-full data-starting-style:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        destructive:
          'border-destructive/80 bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function ToastRoot({
  className,
  variant,
  toast,
  ...props
}: ToastPrimitive.Root.Props & VariantProps<typeof toastVariants>) {
  const resolvedVariant =
    variant ?? (toast.type === 'destructive' ? 'destructive' : 'default')

  return (
    <ToastPrimitive.Root
      data-slot="toast"
      toast={toast}
      className={cn(toastVariants({ variant: resolvedVariant }), className)}
      {...props}
    />
  )
}

function ToastContent({
  className,
  ...props
}: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn('overflow-hidden', className)}
      {...props}
    />
  )
}

function ToastAction({
  className,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      className={cn(
        'hover:bg-secondary focus:ring-ring group-data-[type=destructive]:border-muted/40 group-data-[type=destructive]:hover:border-destructive/30 group-data-[type=destructive]:hover:bg-destructive group-data-[type=destructive]:hover:text-destructive-foreground group-data-[type=destructive]:focus:ring-destructive inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors focus:ring-1 focus:outline-none disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  )
}

function ToastClose({
  className,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      data-slot="toast-close"
      aria-label="Dismiss notification"
      className={cn(
        'text-foreground/55 hover:text-foreground hover:bg-foreground/10 focus-visible:ring-ring focus-visible:ring-offset-background absolute top-1/2 right-2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-md opacity-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'group-data-[type=destructive]:text-destructive-foreground group-data-[type=destructive]:hover:bg-destructive-foreground/15 group-data-[type=destructive]:focus-visible:ring-destructive-foreground/60 group-data-[type=destructive]:focus-visible:ring-offset-destructive',
        className,
      )}
      {...props}
    >
      <X className="h-4 w-4 shrink-0" aria-hidden />
    </ToastPrimitive.Close>
  )
}

function ToastTitle({
  className,
  ...props
}: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn(
        'group-data-[type=destructive]:text-destructive-foreground text-sm font-semibold [&+div]:text-xs',
        className,
      )}
      {...props}
    />
  )
}

function ToastDescription({
  className,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      className={cn(
        'group-data-[type=destructive]:text-destructive-foreground text-sm opacity-90 group-data-[type=destructive]:opacity-95',
        className,
      )}
      {...props}
    />
  )
}

interface ToastProps {
  variant?: 'default' | 'destructive'
  className?: string
  style?: React.CSSProperties
}

type ToastActionElement = React.ReactElement<
  React.ComponentProps<typeof ToastAction>
>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  ToastPortal,
  ToastRoot,
  ToastContent,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
